/** Max response body size when loading HTML from a URL into a tab (keep in sync with server). */
export const MAX_FETCH_HTML_BYTES = 5 * 1024 * 1024;

function fetchHtmlPageApiBase(): string {
  const v = import.meta.env.VITE_FETCH_HTML_PAGE_API as string | undefined;
  if (v && v.trim()) return v.replace(/\/$/, "");
  return "";
}

export function fetchHtmlPageEndpoint(): string {
  const base = fetchHtmlPageApiBase();
  return base ? `${base}/api/fetch-html-page` : "/api/fetch-html-page";
}

/** Parse API response body; surfaces HTML proxy failures as actionable messages. */
export function parseFetchHtmlHttpBody(
  raw: string,
  status: number,
): { ok: true; data: unknown } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    const proxyHint =
      status === 500 || status === 502 || status === 503 || status === 504
        ? " Often the dev proxy could not reach a backend (empty body). With npm run dev / preview, /api/fetch-html-page is handled inside Vite—if you still see this, try restarting dev. Docker/nginx uses the fetch-html service on port 3848."
        : "";
    return {
      ok: false,
      error: `Empty response (HTTP ${status}).${proxyHint} For a custom API host, set VITE_FETCH_HTML_PAGE_API at build time.`,
    };
  }
  try {
    return { ok: true, data: JSON.parse(raw) as unknown };
  } catch {
    const isHtml = /^\s*</.test(trimmed) && /<\/?(html|head|body|!DOCTYPE)/i.test(trimmed.slice(0, 500));
    if (isHtml) {
      return {
        ok: false,
        error: `Fetch API returned a web page (HTTP ${status}), not JSON. Use npm run dev / preview, or set VITE_FETCH_HTML_PAGE_API at build time.`,
      };
    }
    const snippet = trimmed.length > 160 ? `${trimmed.slice(0, 160)}…` : trimmed;
    return { ok: false, error: `Server did not return JSON (HTTP ${status}): ${snippet}` };
  }
}

/** Safe tab title ending in `.html` so language mode is HTML. */
export function tabTitleFromFetchedPageUrl(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    let base = parts.length > 0 ? parts[parts.length - 1]! : "";
    try {
      base = decodeURIComponent(base);
    } catch {
      /* keep */
    }
    base = base.replace(/[/\\?%*:|"<>]/g, "-").trim();
    if (!base) {
      base = u.hostname.replace(/[/\\?%*:|"<>]/g, "-").trim() || "page";
    }
    const dot = base.lastIndexOf(".");
    const stem = dot > 0 ? base.slice(0, dot) : base;
    const safe = (stem || "page").slice(0, 120);
    return `${safe}.html`;
  } catch {
    return "fetched-page.html";
  }
}

export type FetchHtmlPageResult =
  | { ok: true; url: string; text: string }
  | { ok: false; message: string };

export async function fetchHtmlPageText(rawUrl: string): Promise<FetchHtmlPageResult> {
  const trimmed = rawUrl.trim();
  if (!trimmed) return { ok: false, message: "Enter a URL." };
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return { ok: false, message: "That URL is not valid." };
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") {
    return { ok: false, message: "Only http and https URLs are allowed." };
  }
  try {
    const res = await fetch(fetchHtmlPageEndpoint(), {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: u.href }),
    });
    const raw = await res.text();
    const parsed = parseFetchHtmlHttpBody(raw, res.status);
    if (!parsed.ok) return { ok: false, message: parsed.error };
    const data = parsed.data as { error?: string; url?: string; html?: string };
    if (!res.ok) {
      const err = typeof data?.error === "string" && data.error.length ? data.error : `HTTP ${res.status}`;
      return { ok: false, message: err };
    }
    const html = data.html;
    const resolved = typeof data.url === "string" && data.url.length ? data.url : u.href;
    if (typeof html !== "string" || !html.length) {
      return { ok: false, message: "Server returned no HTML body." };
    }
    if (html.length > MAX_FETCH_HTML_BYTES) {
      return { ok: false, message: `Page is too large (>${MAX_FETCH_HTML_BYTES} bytes).` };
    }
    return { ok: true, url: resolved, text: html };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/Failed to fetch|NetworkError|Load failed|networkerror/i.test(msg)) {
      return {
        ok: false,
        message:
          "Network error calling /api/fetch-html-page. With npm run dev or npm run preview, the route is served by Vite. Deployed Docker uses nginx to the fetch-html process; static-only hosting needs VITE_FETCH_HTML_PAGE_API.",
      };
    }
    return { ok: false, message: msg };
  }
}
